import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Brain, Target, ArrowRight, Share2 } from 'lucide-react';
import { OutlineButton } from '../components/ui/buttons/OutlineButton';
import { AccentButton } from '../components/ui/buttons/AccentButton';
import { GradientTitle } from '../components/ui/typography/GradientTitle';
import { ShareModal } from '../components/ui/ShareModal';
import movingApartmentsImage from '../assets/quiz/moving-apartments.jpg';
import socialMediaImage from '../assets/quiz/social-media.jpg';
import snacksImage from '../assets/quiz/snacks.jpg';
import phoneBatteryImage from '../assets/quiz/phone_battery.jpg';
import newsImage from '../assets/quiz/news.jpg';
import driverImage from '../assets/quiz/driver.jpg';
import cosmeticsImage from '../assets/quiz/cosmetics.jpg';

interface Bias {
  name: string;
  id: string;
  description: string;
  category: string;
}

interface BiasCategory {
  title: string;
  description: string;
  biases: Record<string, Bias>;
}

interface BiasInfo {
  [key: string]: BiasCategory;
}

interface Question {
  id: number;
  question: string;
  options: string[];
  category: string;
  bias: string;
}

// Add this constant for consistent transition duration
const TRANSITION_DURATION = 300;

export const QuizPage = () => {
  // Funny bias categories based on score and dominant bias category
  const getBiasCategory = (score: number, dominantCategory: string) => {
    if (score >= 80) {
      switch (dominantCategory) {
        case 'Decision-Making':
          return "The Professional Procrastinator";
        case 'Memory':
          return "The Nostalgia Time Traveler";
        case 'Perceptual':
          return "The Skeptical Squinter";
        case 'Reasoning':
          return "The Stubborn Debater";
        case 'Social':
          return "The Hasty Judge";
        default:
          return "The Bias Collector";
      }
    } else if (score >= 60) {
      switch (dominantCategory) {
        case 'Decision-Making':
          return "The Indecisive Shopper";
        case 'Memory':
          return "The Selective Historian";
        case 'Perceptual':
          return "The Glass-Half-Something Viewer";
        case 'Reasoning':
          return "The Opinionated Observer";
        case 'Social':
          return "The Social Media Expert";
        default:
          return "The Casual Assumer";
      }
    } else if (score >= 40) {
      switch (dominantCategory) {
        case 'Decision-Making':
          return "The Occasional Overthinker";
        case 'Memory':
          return "The Digital Photo Reliant";
        case 'Perceptual':
          return "The Pause-Before-Judgment Ponderer";
        case 'Reasoning':
          return "The Second-Thought Thinker";
        case 'Social':
          return "The Benefit-of-Doubt Giver";
        default:
          return "The Middle-Ground Maintainer";
      }
    } else {
      return "The Critical Thinking Champion";
    }
  };

  // State for tracking current question, answers, and whether quiz is complete
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [biasScores, setBiasScores] = useState<Record<string, number>>({});
  const [quizComplete, setQuizComplete] = useState(false);
  const [selectedBiases, setSelectedBiases] = useState<string[]>([]);
  const [dominantCategory, setDominantCategory] = useState<string>('');
  const [overallScore, setOverallScore] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);

  // Quiz questions with all 10 questions
  const questions: Question[] = [
    // Decision-Making Biases (2)
    {
      id: 1,
      question: "You've watched 6 episodes of a new Netflix series and it's not great. What do you do?",
      options: [
        "Keep watching to finish the season - I've already invested this much time",
        "Watch one more episode before deciding to quit",
        "Stop watching and find something else",
        "Tell everyone how bad it is, but secretly finish it anyway"
      ],
      category: "Decision-Making",
      bias: "Sunk Cost Fallacy"
    },
    {
      id: 2,
      question: "Your favorite coffee shop changed their menu and removed your go-to order. What's your reaction?",
      options: [
        "Order the same thing from a different shop, even if it's farther away",
        "Complain to the barista about how you've been coming here for years",
        "Try something new on their menu that sounds interesting",
        "See what other regulars are ordering and consider their choices"
      ],
      category: "Decision-Making",
      bias: "Status Quo Bias"
    },
    {
      id: 3,
      question: "How do you remember your high school or college days?",
      options: [
        "They were the best days of my life - nothing but great memories",
        "I mainly remember the fun parties and friendships, not the exams",
        "A mix of good times, stress, awkwardness, and growth",
        "I try to recall both the cringe moments and good times equally"
      ],
      category: "Memory",
      bias: "Rosy Retrospection"
    },
    {
      id: 4,
      question: "Your friend insists you promised to help them move this weekend, but you don't remember agreeing to it. What's your reaction?",
      options: [
        "I definitely didn't promise that - they must be mistaken",
        "Check my messages to see if I actually agreed to it",
        "Consider that I might have forgotten and apologize",
        "Ask them when we discussed it to see if it jogs my memory"
      ],
      category: "Memory",
      bias: "Hindsight Bias"
    },
    // Perceptual Biases (2)
    {
      id: 5,
      question: "You post a photo on social media that gets 50 likes and one negative comment. What sticks with you?",
      options: [
        "That one rude comment - it ruins the whole experience",
        "I focus mostly on addressing or defending against the criticism",
        "I appreciate the likes but wonder why someone didn't like it",
        "I'm happy with the overall positive response and don't dwell on one comment"
      ],
      category: "Perceptual",
      bias: "Negativity Bias"
    },
    {
      id: 6,
      question: "You see a headline: 'Study Shows Your Favorite Snack May Boost Energy'. Without reading the article, what's your thought?",
      options: [
        "I knew it was healthy! I'll eat more of it now",
        "That confirms what I've been saying all along",
        "I wonder what the actual study showed and how it was conducted",
        "Interesting, but I'd need to see multiple studies before changing my habits"
      ],
      category: "Perceptual",
      bias: "Selective Perception"
    },
    // Reasoning Biases (2)
    {
      id: 7,
      question: "You notice your phone battery drains faster after installing a new app. What's your conclusion?",
      options: [
        "That app is definitely killing my battery",
        "It must be that app since nothing else has changed",
        "It could be the app, or maybe my battery is just getting older",
        "I should check battery usage stats before blaming the app"
      ],
      category: "Reasoning",
      bias: "Base Rate Fallacy"
    },
    {
      id: 8,
      question: "You read an online article that challenges your views on your favorite diet. What do you do?",
      options: [
        "This article is obviously biased - the author probably has an agenda",
        "Look for flaws in their argument so I can dismiss it",
        "Read it but remind myself of all the reasons my diet is still the best",
        "Consider the evidence presented and reflect on whether I should adjust my views"
      ],
      category: "Reasoning",
      bias: "Backfire Effect"
    },
    // Social Biases (2)
    {
      id: 9,
      question: "Your Uber driver is late picking you up. What's your first thought?",
      options: [
        "They're probably just a lazy or bad driver",
        "They're inconsiderate of other people's time",
        "Maybe there's traffic or an accident I don't know about",
        "Could be various reasons - bad navigation, traffic, or a previous rider delay"
      ],
      category: "Social",
      bias: "Fundamental Attribution Error"
    },
    {
      id: 10,
      question: "Your favorite actor launches a skincare line. What's your impression of it?",
      options: [
        "It must be amazing - they always look great!",
        "I'd trust it more than a random brand I see at the store",
        "I'll check the reviews but I'm already interested",
        "I'd research the ingredients and price just like any other product"
      ],
      category: "Social",
      bias: "Halo Effect"
    }
  ];

  // Results data with all biases
  const biasInfo: BiasInfo = {
    "Decision-Making": {
      title: "Decision-Making Biases",
      description: "You might be especially prone to biases that affect how you make choices and evaluate options.",
      biases: {
        "bias-sunk-cost": {
          name: "Sunk Cost Fallacy",
          id: "bias-sunk-cost",
          description: "We irrationally cling to choices we've already invested in—whether time, money, or effort—despite diminishing returns.",
          category: "Decision-Making"
        },
        "bias-status-quo": {
          name: "Status Quo Bias",
          id: "bias-status-quo",
          description: "Our innate preference for keeping things as they are often overpowers the potential benefits of change.",
          category: "Decision-Making"
        }
      }
    },
    "Memory": {
      title: "Memory Biases",
      description: "Your thinking patterns suggest you might experience biases related to how you remember and recall events.",
      biases: {
        "bias-rosy-retrospection": {
          name: "Rosy Retrospection",
          id: "bias-rosy-retrospection",
          description: "Our minds paint the past with a deceptively positive brush, minimizing struggles and amplifying joys.",
          category: "Memory"
        },
        "bias-hindsight": {
          name: "Hindsight Bias",
          id: "bias-hindsight",
          description: "We often claim events were predictable after they've happened, overestimating our forecasting abilities.",
          category: "Memory"
        }
      }
    },
    "Perceptual": {
      title: "Perceptual Biases",
      description: "You may be particularly susceptible to biases that affect how you perceive information and experiences.",
      biases: {
        "bias-negativity": {
          name: "Negativity Bias",
          id: "bias-negativity",
          description: "Our brains are wired to react more strongly to negative experiences than positive ones.",
          category: "Perceptual"
        },
        "bias-selective-perception": {
          name: "Selective Perception",
          id: "bias-selective-perception",
          description: "We naturally filter information through the lens of our existing beliefs, focusing on what confirms our views.",
          category: "Perceptual"
        }
      }
    },
    "Reasoning": {
      title: "Reasoning Biases",
      description: "Your thought patterns suggest a tendency toward biases that affect how you evaluate information and draw conclusions.",
      biases: {
        "bias-base-rate": {
          name: "Base Rate Fallacy",
          id: "bias-base-rate",
          description: "We're naturally drawn to specific, vivid details while ignoring broader statistical realities.",
          category: "Reasoning"
        },
        "bias-backfire-effect": {
          name: "Backfire Effect",
          id: "bias-backfire-effect",
          description: "When presented with evidence that contradicts our beliefs, we often reject it and strengthen our original position.",
          category: "Reasoning"
        }
      }
    },
    "Social": {
      title: "Social Biases",
      description: "You may be especially prone to biases that influence how you interpret the behavior of others and navigate social situations.",
      biases: {
        "bias-fundamental-attribution": {
          name: "Fundamental Attribution Error",
          id: "bias-fundamental-attribution",
          description: "We're quick to blame others' personality flaws for their actions, yet we rarely consider the situational pressures they face.",
          category: "Social"
        },
        "bias-halo-effect": {
          name: "Halo Effect",
          id: "bias-halo-effect",
          description: "We tend to let one positive trait create a golden glow around a person or brand, causing us to assume excellence in unrelated areas.",
          category: "Social"
        }
      }
    }
  };

  // Handle answer selection
  const handleAnswer = (optionIndex: number) => {
    // Set the selected option
    setSelectedOption(optionIndex);
    
    // Process the answer immediately
    const question = questions[currentQuestion];
    const updatedScores = { ...biasScores };
    
    if (!updatedScores[question.category]) {
      updatedScores[question.category] = 0;
    }
    
    // First option typically shows more bias
    const biasScore = 3 - optionIndex;
    updatedScores[question.category] += Math.max(0, biasScore);
    
    setBiasScores(updatedScores);
    
    // Move to next question or complete quiz
    if (currentQuestion < questions.length - 1) {
      // Reset selected option and move to next question
      setTimeout(() => {
        setSelectedOption(null);
        setCurrentQuestion(currentQuestion + 1);
      }, 300);
    } else {
      // Calculate final results
      let maxScore = 0;
      let dominantCat = '';
      let totalScore = 0;
      
      Object.entries(updatedScores).forEach(([category, score]) => {
        if (score > maxScore) {
          maxScore = score;
          dominantCat = category;
        }
        totalScore += score;
      });

      // Calculate overall score as a percentage of maximum possible score
      const maxPossibleScore = questions.length * 3; // 3 is max score per question
      const overallPercentage = (totalScore / maxPossibleScore) * 100;
      
      setOverallScore(overallPercentage);
      setDominantCategory(dominantCat);
      
      // Select biases to show based on scores
      const selectedBiases = Object.entries(updatedScores)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3)
        .map(([category]) => {
          const categoryBiases = Object.keys(biasInfo[category].biases);
          return categoryBiases[Math.floor(Math.random() * categoryBiases.length)];
        });
      
      setSelectedBiases(selectedBiases);
      setQuizComplete(true);
    }
  };

  // Restart the quiz
  const handleRestart = () => {
    setCurrentQuestion(0);
    setBiasScores({});
    setQuizComplete(false);
    setSelectedBiases([]);
    setDominantCategory('');
    setOverallScore(0);
  };

  // Get share text for quiz results
  const getShareText = () => {
    if (!quizComplete) return 'Take the Cognitive Bias Quiz to discover how biases influence your thinking!';
    
    const biasCategory = getBiasCategory(overallScore, dominantCategory);
    const correctAnswers = Math.round((100 - overallScore) / 10);
    
    return `I did the Cognitive Bias Quiz and here are my results, try it yourself!\n\nScore: ${correctAnswers}/10\n${biasCategory}`;
  };
  
  // Get share URL for quiz results
  const getShareUrl = () => {
    const baseUrl = process.env.NODE_ENV !== 'production' ? 'http://localhost:5176' : 'https://www.cognitivebiaslab.com';
    return `${baseUrl}/quiz/`;
  };

  // View for displaying a question
  const renderQuestion = () => {
    const question = questions[currentQuestion];
    
    return (
      <div className="space-y-6">
        {/* Progress header */}
        <div className="sticky top-4 z-30">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center justify-between p-3 md:p-4">
              <div className="mb-2 md:mb-0">
                <h2 className="text-lg md:text-xl font-semibold">Question {currentQuestion + 1} of {questions.length}</h2>
              </div>
              
              {/* Progress bar */}
              <div className="flex items-center gap-3">
                <div className="w-full md:w-48 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-orange-500 to-pink-500 will-change-transform"
                    style={{ 
                      width: `${(currentQuestion / questions.length) * 100}%`,
                      transition: `width ${TRANSITION_DURATION}ms ease-in-out`
                    }}
                  />
                </div>
                <span className="text-sm font-medium text-gray-600 whitespace-nowrap">
                  {Math.round((currentQuestion / questions.length) * 100)}%
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-4 md:p-8 bg-white rounded-xl shadow-md border border-orange-100 mt-6 md:mt-8">
          {currentQuestion === 0 && (
            <div className="mb-6 flex justify-center">
              <figure className="relative w-full sm:w-3/4">
                <img 
                  src="/assets/quiz/optimized/Netflix-lg.jpeg" 
                  alt="TV screen showing Netflix interface with Friends TV show ready to play"
                  className="w-full h-auto rounded-lg object-cover aspect-video shadow-md"
                  width="960" 
                  height="640"
                  loading="eager"
                />
                <figcaption className="mt-2 text-xs text-gray-500">
                  Photo by <a href="https://unsplash.com/@mahrous_houses?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Mahrous Houses</a> on <a href="https://unsplash.com/photos/black-flat-screen-tv-turned-on-near-brown-brick-wall-GHX68AmE1gQ?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Unsplash</a>
                </figcaption>
              </figure>
            </div>
          )}
          
          {currentQuestion === 1 && (
            <div className="mb-6 flex justify-center">
              <figure className="relative w-full sm:w-3/4">
                <img 
                  src="/assets/quiz/optimized/coffee-shop-lg.jpeg" 
                  alt="Stylish coffee shop interior with white brick walls, wooden counter, and hanging pendant lights"
                  className="w-full h-auto rounded-lg object-cover aspect-video shadow-md"
                  width="960" 
                  height="540"
                  loading="lazy"
                />
                <figcaption className="mt-2 text-xs text-gray-500">
                  Photo by <a href="https://unsplash.com/@mahrous_houses?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Mahrous Houses</a> on <a href="https://unsplash.com/photos/black-flat-screen-tv-turned-on-near-brown-brick-wall-GHX68AmE1gQ?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Unsplash</a>
                </figcaption>
              </figure>
            </div>
          )}
          
          {currentQuestion === 2 && (
            <div className="mb-6 flex justify-center">
              <figure className="relative w-full sm:w-3/4">
                <img 
                  src="/assets/quiz/optimized/College-lg.jpeg" 
                  alt="Group of college students at a party with string lights in the background"
                  className="w-full h-auto rounded-lg object-cover aspect-video shadow-md"
                  width="960" 
                  height="540"
                  loading="lazy"
                />
                <figcaption className="mt-2 text-xs text-gray-500">
                  Photo by <a href="https://unsplash.com/@srosinger3997?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Samantha Gades</a> on <a href="https://unsplash.com/photos/laughing-people-in-party-fIHozNWfcvs?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Unsplash</a>
                </figcaption>
              </figure>
            </div>
          )}
          
          {currentQuestion === 3 && (
            <div className="mb-6 flex justify-center">
              <figure className="relative w-full sm:w-3/4">
                <img 
                  src={movingApartmentsImage} 
                  alt="Stacked moving boxes in an empty apartment ready for relocation"
                  className="w-full h-auto rounded-lg object-cover aspect-video shadow-md"
                  width="1000" 
                  height="667"
                  loading="eager"
                />
                <figcaption className="mt-2 text-xs text-gray-500">
                  Photo by <a href="https://unsplash.com/@balogmih?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Michal Balog</a> on <a href="https://unsplash.com/photos/66JMudIjDTw?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Unsplash</a>
                </figcaption>
              </figure>
            </div>
          )}
          
          {currentQuestion === 4 && (
            <div className="mb-6 flex justify-center">
              <figure className="relative w-full sm:w-3/4">
                <img 
                  src={socialMediaImage} 
                  alt="Person using a laptop showing Facebook profile while also checking their smartphone"
                  className="w-full h-auto rounded-lg object-cover aspect-video shadow-md"
                  width="1000" 
                  height="667"
                  loading="eager"
                />
                <figcaption className="mt-2 text-xs text-gray-500">
                  Photo by <a href="https://unsplash.com/@austindistel?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:underline">Austin Distel</a> on <a href="https://unsplash.com/photos/a-woman-sitting-at-a-table-with-a-laptop-computer-wawEfYdpkag?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:underline">Unsplash</a>
                </figcaption>
              </figure>
            </div>
          )}
          
          {currentQuestion === 5 && (
            <div className="mb-6 flex justify-center">
              <figure className="relative w-full sm:w-3/4">
                <img 
                  src={snacksImage} 
                  alt="Box filled with various chocolate bars and candy including KitKat, Snickers, Mars, Twix, and other popular snack brands"
                  className="w-full h-auto rounded-lg object-cover aspect-video shadow-md"
                  width="1000" 
                  height="667"
                  loading="eager"
                />
                <figcaption className="mt-2 text-xs text-gray-500">
                  Photo by <a href="https://unsplash.com/@redaquamedia?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Denny Müller</a> on <a href="https://unsplash.com/photos/orange-and-red-plastic-pack-mGP8gyGb8zY?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Unsplash</a>
                </figcaption>
              </figure>
            </div>
          )}
          
          {currentQuestion === 6 && (
            <div className="mb-6 flex justify-center">
              <figure className="relative w-full sm:w-3/4">
                <img 
                  src={phoneBatteryImage} 
                  alt="Hand holding smartphone displaying lock screen with 90% battery level and blue background"
                  className="w-full h-auto rounded-lg object-cover aspect-video shadow-md"
                  width="1000" 
                  height="667"
                  loading="eager"
                />
                <figcaption className="mt-2 text-xs text-gray-500">
                  Photo by <a href="https://unsplash.com/@onurbinay?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Onur Binay</a> on <a href="https://unsplash.com/photos/person-holding-black-samsung-android-smartphone-phQnS6PwPBI?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Unsplash</a>
                </figcaption>
              </figure>
            </div>
          )}
          
          {currentQuestion === 7 && (
            <div className="mb-6 flex justify-center">
              <figure className="relative w-full sm:w-3/4">
                <img 
                  src={newsImage} 
                  alt="Person reading a newspaper while sitting on a bench, holding the paper open in front of them"
                  className="w-full h-auto rounded-lg object-cover aspect-video shadow-md"
                  width="1000" 
                  height="667"
                  loading="eager"
                />
                <figcaption className="mt-2 text-xs text-gray-500">
                  Photo by <a href="https://unsplash.com/@iamromankraft?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Roman Kraft</a> on <a href="https://unsplash.com/photos/man-sitting-on-bench-reading-newspaper-_Zua2hyvTBk?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Unsplash</a>
                </figcaption>
              </figure>
            </div>
          )}
          
          {currentQuestion === 8 && (
            <div className="mb-6 flex justify-center">
              <figure className="relative w-full sm:w-3/4">
                <img 
                  src={driverImage} 
                  alt="Driver using navigation app on smartphone mounted in car while driving"
                  className="w-full h-auto rounded-lg object-cover aspect-video shadow-md"
                  width="1000" 
                  height="667"
                  loading="eager"
                />
                <figcaption className="mt-2 text-xs text-gray-500">
                  Photo by <a href="https://unsplash.com/@plhnk?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Paul Hanaoka</a> on <a href="https://unsplash.com/photos/person-holding-iphone-6-inside-car-D-qq7W751vs?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Unsplash</a>
                </figcaption>
              </figure>
            </div>
          )}
          
          {currentQuestion === 9 && (
            <div className="mb-6 flex justify-center">
              <figure className="relative w-full sm:w-3/4">
                <img 
                  src={cosmeticsImage} 
                  alt="Flatlay of various makeup products including eyeshadow palette, foundation, mascara and makeup brushes"
                  className="w-full h-auto rounded-lg object-cover aspect-video shadow-md"
                  width="1000" 
                  height="667"
                  loading="eager"
                />
                <figcaption className="mt-2 text-xs text-gray-500">
                  Photo by <a href="https://unsplash.com/@jazminantoinette?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Jazmin Quaynor</a> on <a href="https://unsplash.com/photos/close-up-photography-of-assorted-cosmetics-FoeIOgztCXo?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash" className="text-gray-600 hover:underline" target="_blank" rel="noopener noreferrer">Unsplash</a>
                </figcaption>
              </figure>
            </div>
          )}
          
          <p className="text-lg md:text-xl font-medium mb-6 md:mb-8 text-gray-800">{question.question}</p>
          
          <div className="space-y-3 md:space-y-4">
            {question.options.map((option, index) => (
              <button
                key={`question-${currentQuestion}-option-${index}`}
                onClick={(e) => {
                  // Explicitly remove focus after click
                  e.currentTarget.blur();
                  handleAnswer(index);
                }}
                className={`w-full text-left p-3 md:p-5 border-2 rounded-xl transition-all duration-300 group flex items-center
                  ${selectedOption === index 
                    ? 'bg-gradient-to-r from-orange-50 to-pink-50 border-orange-300' 
                    : 'border-gray-200 hover:bg-gradient-to-r hover:from-orange-50 hover:to-pink-50 hover:border-orange-300'}`}
              >
                <span className={`flex-shrink-0 w-7 h-7 md:w-8 md:h-8 text-sm font-medium rounded-lg mr-3 md:mr-4 flex items-center justify-center
                  ${selectedOption === index ? 'bg-orange-200 text-orange-800' : 'bg-orange-100 text-orange-800 group-hover:bg-orange-200'}`}>
                  {String.fromCharCode(65 + index)}
                </span>
                <span className="text-sm md:text-base">{option}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // View for displaying the result
  const renderResult = () => {
    const biasCategory = getBiasCategory(overallScore, dominantCategory);
    const correctAnswers = Math.round((100 - overallScore) / 10);
    
    return (
      <>
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          shareUrl={getShareUrl()}
          shareText={getShareText()}
          title="Share Your Quiz Results"
        />
        
        <div className="space-y-6 md:space-y-8">
          <div className="p-4 md:p-8 bg-white rounded-xl shadow-md border border-orange-100">
            <div className="mb-4 md:mb-6 text-center">
              <span className="inline-block px-4 md:px-6 py-1 md:py-2 bg-gradient-to-r from-orange-100 to-pink-100 text-orange-800 rounded-full text-base md:text-lg font-medium">
                Your Results
              </span>
            </div>
            
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-4 mb-6">
                <Target className="w-6 h-6 text-orange-600" />
                <span className="text-2xl font-bold text-gray-900">
                  Score: {correctAnswers}/10
                </span>
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 via-pink-600 to-red-600 bg-clip-text text-transparent mb-4">
                {biasCategory}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Based on your answers, you show a particular inclination towards {dominantCategory.toLowerCase()} biases. 
                Here's what we discovered about your thinking patterns:
              </p>
              
              {/* Share button */}
              <div className="flex justify-center">
                <AccentButton 
                  onClick={() => setIsShareModalOpen(true)}
                  className="mt-6"
                  aria-label="Share your quiz results"
                >
                  <div className="flex items-center space-x-2">
                    <Share2 className="w-4 h-4" aria-hidden="true" />
                    <span>Share Your Results</span>
                  </div>
                </AccentButton>
              </div>
            </div>
            
            <div className="mt-12">
              <h3 className="text-xl font-semibold mb-6 text-gray-800">Your cognitive toolkit includes:</h3>
              <div className="space-y-4">
                {selectedBiases.map((biasId) => {
                  let bias = null;
                  for (const category in biasInfo) {
                    if (biasInfo[category].biases[biasId]) {
                      bias = biasInfo[category].biases[biasId];
                      break;
                    }
                  }
                  
                  if (!bias) return null;
                  
                  return (
                    <a
                      key={biasId}
                      href={`/bias/${bias.id}/`}
                      target="_blank"
                      rel="noopener noreferrer" 
                      className="bg-white/95 backdrop-blur-sm py-5 px-4 sm:px-6 rounded-3xl shadow-md border border-gray-100
                        hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 group cursor-pointer w-full block"
                    >
                      <div className="flex flex-row items-start gap-3 sm:gap-5">
                        <div className="flex-1 min-w-0 space-y-2.5">
                          <div className="flex flex-row items-center justify-between gap-2 flex-wrap">
                            <h4 className="text-lg sm:text-xl font-bold text-gray-800 break-words hyphens-auto">{bias.name}</h4>
                            <span className="text-xs sm:text-sm font-medium text-orange-500 bg-orange-50 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full whitespace-nowrap">
                              {bias.category}
                            </span>
                          </div>
                          
                          <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{bias.description}</p>

                          <div className="pt-0.5">
                            <span className="text-orange-500 font-medium flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                              Learn More <ArrowRight className="w-4 h-4" />
                            </span>
                          </div>
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
            
            <div className="mt-12 flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
              <OutlineButton
                onClick={handleRestart}
                active={false}
                className="w-[85%] sm:w-auto mx-auto"
              >
                Take the Quiz Again
              </OutlineButton>
              <Link to="/learn/" className="w-[85%] sm:w-auto mx-auto">
                <AccentButton className="w-full">
                  Start Learning
                </AccentButton>
              </Link>
            </div>
          </div>
          
          <div className="p-6 bg-gradient-to-r from-orange-50/50 to-pink-50/50 rounded-xl border border-orange-100">
            <h3 className="text-lg font-semibold mb-3 text-orange-800">Remember!</h3>
            <p className="text-gray-700">
              Everyone experiences cognitive biases - they're simply how our brains process information efficiently. 
              Awareness is the first step toward more balanced thinking. Keep exploring and practicing to improve your critical thinking skills!
            </p>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>Interactive Cognitive Bias Quiz | Test Your Decision Making </title>
        <meta 
          name="description" 
          content="Take our interactive quiz to discover how cognitive biases influence your decisions. Learn about memory, social, and reasoning biases." 
        />
        <meta 
          name="keywords" 
          content="cognitive bias quiz, decision making test, thinking patterns, psychological biases, critical thinking assessment, cognitive psychology test" 
        />
        <link rel="canonical" href="https://www.cognitivebiaslab.com/quiz/" key="canonical" data-source="page-component" />
        
        {/* OpenGraph tags for better social sharing */}
        <meta property="og:title" content="Interactive Cognitive Bias Quiz | Test Your Decision Making" />
        <meta 
          property="og:description" 
          content="Take our interactive quiz to discover how cognitive biases influence your decisions. Test your critical thinking with real-world scenarios." 
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cognitivebiaslab.com/quiz/" />
        <meta property="og:site_name" content="Cognitive Bias Lab" />
        <meta property="og:image" content="https://www.cognitivebiaslab.com/logo.webp" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:type" content="image/webp" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Interactive Cognitive Bias Quiz | Test Your Decision Making" />
        <meta 
          name="twitter:description" 
          content="Take our interactive quiz to discover how cognitive biases influence your decisions. Test your critical thinking with real-world scenarios." 
        />

        {/* Additional meta tags for better SEO */}
        <meta name="robots" content="index, follow" />
        <meta 
          name="author" 
          content="Cognitive Bias Lab" 
        />
        
        {/* Structured data for Quiz */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Quiz",
            "name": "Cognitive Bias Quiz",
            "about": {
              "@type": "Thing",
              "name": "Cognitive Biases",
              "description": "Mental shortcuts and patterns that can influence decision making"
            },
            "educationalAlignment": {
              "@type": "AlignmentObject",
              "alignmentType": "educationalSubject",
              "targetName": "Psychology"
            },
            "provider": {
              "@type": "Organization",
              "name": "Cognitive Bias Lab",
              "url": "https://www.cognitivebiaslab.com"
            },
            "numberOfQuestions": "10",
            "timeRequired": "PT10M",
            "isAccessibleForFree": true
          })}
        </script>
      </Helmet>
      <div className="max-w-4xl mx-auto py-6 md:py-12 px-4">
        <div className="text-center mb-8 md:mb-12">
          <GradientTitle className="text-3xl md:text-4xl">What's In Your Mental Toolkit?</GradientTitle>
          <p className="text-lg md:text-xl text-gray-600 mt-4">Discover which cognitive biases might be influencing your thinking</p>
        </div>
        
        {!quizComplete ? renderQuestion() : renderResult()}
      </div>
    </>
  );
};

export default QuizPage; 